import React, { useState, useEffect } from 'react';
import { X, Check, Columns, Save, RotateCcw, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { authedFetch } from '../lib/db';

export interface ColumnSetting {
  id: string;
  label: string;
  visible: boolean;
}

export const DEFAULT_COLUMNS: ColumnSetting[] = [
  { id: 'lead', label: 'Lead', visible: true },
  { id: 'consigneeName', label: 'Consignee', visible: true },
  { id: 'shipperName', label: 'Shipper', visible: true },
  { id: 'status', label: 'Status', visible: true },
  { id: 'contactPerson', label: 'Contact', visible: true },
  { id: 'phone', label: 'Phone', visible: true },
  { id: 'email', label: 'Email', visible: true },
  { id: 'owner', label: 'Salesperson', visible: true },
  { id: 'leadType', label: 'Lead Type', visible: true },
  { id: 'notes', label: 'Lead Notes', visible: true },
  { id: 'serviceTypes', label: 'Service Types', visible: true },
  { id: 'industries', label: 'Industries', visible: true },
  { id: 'outboundStates', label: 'Outbound States', visible: true },
  { id: 'outboundWarehouses', label: 'Outbound Warehouses', visible: true },
  { id: 'daysOfService', label: 'Days of Service', visible: true },
  { id: 'fullQuoteView', label: 'Full Quote View', visible: true },
  { id: 'estimatedDealValue', label: 'Estimated Deal Value', visible: true },
  { id: 'lastUpdateDate', label: 'Last Update Date', visible: true },
  { id: 'consigneeAccount', label: 'Consignee Account', visible: false },
  { id: 'shipperAccount', label: 'Shipper Account', visible: false },
  { id: 'careOf', label: 'Care Of', visible: false },
  { id: 'billToAccountNumber', label: 'Bill-To Account #', visible: false },
  { id: 'creditAppSentAt', label: 'Credit App Sent', visible: false },
  { id: 'rateAgreementSentAt', label: 'Rate Agreement Sent', visible: false },
  { id: 'internalEmailsSentAt', label: 'Internal Emails Sent', visible: false },
  { id: 'firstDeliveryFollowUpAt', label: 'First Delivery Follow Up', visible: false },
  { id: 'actions', label: 'Actions', visible: true },
];

export function normalizeColumnPreferences(cols?: ColumnSetting[]): ColumnSetting[] {
  if (!cols || !Array.isArray(cols) || cols.length === 0) {
    return DEFAULT_COLUMNS;
  }

  const validIds = new Set(DEFAULT_COLUMNS.map((c) => c.id));
  const defaultLabelMap = new Map(DEFAULT_COLUMNS.map((c) => [c.id, c.label]));
  const defaultVisibilityMap = new Map(DEFAULT_COLUMNS.map((c) => [c.id, c.visible]));

  const result: ColumnSetting[] = [];
  const seenIds = new Set<string>();

  for (const col of cols) {
    let targetId = col.id;
    // Map legacy ids
    if (targetId === 'leadName' || targetId === 'company') {
      targetId = 'lead';
    } else if (targetId === 'nextSteps') {
      targetId = 'notes';
    }

    if (!validIds.has(targetId) || seenIds.has(targetId)) {
      continue;
    }

    seenIds.add(targetId);
    result.push({
      id: targetId,
      label: defaultLabelMap.get(targetId) || col.label,
      visible: typeof col.visible === 'boolean' ? col.visible : (defaultVisibilityMap.get(targetId) ?? true),
    });
  }

  // Append any missing DEFAULT_COLUMNS in their standard default order
  for (const defCol of DEFAULT_COLUMNS) {
    if (!seenIds.has(defCol.id)) {
      result.push({ ...defCol });
      seenIds.add(defCol.id);
    }
  }

  return result;
}

interface ColumnCustomizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  onSavePreferences: (cols: ColumnSetting[]) => void;
  initialColumns?: ColumnSetting[];
}

export default function ColumnCustomizerModal({
  isOpen,
  onClose,
  userEmail,
  onSavePreferences,
  initialColumns,
}: ColumnCustomizerModalProps) {
  const [columns, setColumns] = useState<ColumnSetting[]>(() => normalizeColumnPreferences(initialColumns));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setColumns(normalizeColumnPreferences(initialColumns));
  }, [initialColumns, isOpen]);

  if (!isOpen) return null;

  const toggleVisibility = (id: string) => {
    setColumns((prev) =>
      prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col))
    );
  };

  const moveColumn = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= columns.length) return;
    const updated = [...columns];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, moved);
    setColumns(updated);
  };

  const handleReset = () => {
    setColumns(DEFAULT_COLUMNS);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authedFetch('/api/user-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail, columnPreferences: columns }),
      });
      onSavePreferences(columns);
      onClose();
    } catch (err) {
      console.error('Failed to save column preferences:', err);
      onSavePreferences(columns);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900/80">
          <div className="flex items-center gap-2">
            <Columns className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">
              Customize & Reorder Dashboard Columns
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto max-h-[60vh] space-y-2">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Toggle visibility and use arrow controls to reorder columns on your pipeline table.
          </p>

          {columns.map((col, idx) => (
            <div
              key={col.id}
              className={`flex items-center justify-between p-2.5 rounded-lg border transition select-none ${
                col.visible
                  ? 'border-blue-200 bg-blue-50/50 dark:border-blue-900/60 dark:bg-blue-950/30'
                  : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 opacity-60'
              }`}
            >
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  {col.label}
                  {col.id === 'notes' && (
                    <span className="ml-1.5 text-[10px] font-extrabold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-950 px-1.5 py-0.5 rounded">
                      Required
                    </span>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 overflow-hidden">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => moveColumn(idx, 'up')}
                    className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Move Up"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    disabled={idx === columns.length - 1}
                    onClick={() => moveColumn(idx, 'down')}
                    className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Move Down"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>

                <input
                  type="checkbox"
                  checked={col.visible}
                  onChange={() => toggleVisibility(col.id)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 transition"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset Defaults
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save View'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
